import React from 'react';
import { Button } from '@/components/ui/button';
import { 
  Monitor, 
  Tablet, 
  Smartphone, 
  RefreshCw, 
  MousePointer2, 
  Loader2, 
  Check, 
  Sparkles,
  Eye,
  Code
} from 'lucide-react';

/**
 * PreviewToolbar - Toolbar with view modes, responsive controls, and actions
 * Extracted from DesignPreviewPage.js
 */
const PreviewToolbar = ({
  // Responsive view
  responsiveViewMode,
  setResponsiveViewMode,
  // Refresh
  onRefresh,
  // Inspect mode
  inspectMode,
  onToggleInspectMode,
  iframeReady,
  previewLoading,
  // Images
  imageSource,
  updatingImages,
  onUpdateImages,
  // View mode
  viewMode,
  setViewMode,
}) => {
  return (
    <div className="flex items-center gap-2">
      {/* Responsive View Mode */}
      <div className="flex gap-1 bg-zinc-100 rounded-lg p-1">
        <button
          onClick={() => setResponsiveViewMode('desktop')}
          className={`p-2 rounded ${responsiveViewMode === 'desktop' ? 'bg-white shadow-sm' : 'hover:bg-zinc-200'}`}
          title="Desktop view"
          data-testid="desktop-view-btn"
        >
          <Monitor className="w-4 h-4" />
        </button>
        <button
          onClick={() => setResponsiveViewMode('tablet')}
          className={`p-2 rounded ${responsiveViewMode === 'tablet' ? 'bg-white shadow-sm' : 'hover:bg-zinc-200'}`}
          title="Tablet view"
          data-testid="tablet-view-btn"
        >
          <Tablet className="w-4 h-4" />
        </button>
        <button
          onClick={() => setResponsiveViewMode('mobile')}
          className={`p-2 rounded ${responsiveViewMode === 'mobile' ? 'bg-white shadow-sm' : 'hover:bg-zinc-200'}`}
          title="Mobile view"
          data-testid="mobile-view-btn"
        >
          <Smartphone className="w-4 h-4" />
        </button>
      </div>
      
      <div className="w-px h-6 bg-zinc-200" />
      
      <Button
        variant="ghost"
        size="sm"
        onClick={onRefresh}
        title="Refresh preview"
        data-testid="refresh-preview-btn"
      >
        <RefreshCw className="w-4 h-4" />
      </Button>
      
      {/* Inspect Mode Toggle */}
      <Button
        variant={inspectMode ? 'default' : 'outline'}
        size="sm"
        onClick={onToggleInspectMode}
        title={inspectMode ? 'Exit inspect mode' : 'Click elements to edit them'}
        className={inspectMode ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'text-zinc-700 border-purple-300 hover:bg-purple-50'}
        data-testid="inspect-mode-btn"
        disabled={!iframeReady || previewLoading}
      >
        <MousePointer2 className={`w-4 h-4 ${inspectMode ? '' : 'mr-2'}`} />
        {!inspectMode && 'Select Element'}
      </Button>
      
      <Button
        variant={imageSource === 'unsplash' ? 'outline' : 'default'}
        size="sm"
        onClick={onUpdateImages}
        disabled={updatingImages}
        title={imageSource === 'unsplash' ? 'Images already updated from Unsplash' : 'Update images with business-relevant visuals from Unsplash'}
        className={imageSource === 'unsplash' ? 'text-green-600 border-green-300' : 'bg-amber-500 hover:bg-amber-600 text-white'}
        data-testid="update-images-btn"
      >
        {updatingImages ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Updating...
          </>
        ) : imageSource === 'unsplash' ? (
          <>
            <Check className="w-4 h-4 mr-2" />
            Images Updated
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            Update Images
          </>
        )}
      </Button>
      
      <div className="w-px h-6 bg-zinc-200" />
      
      <div className="flex gap-2">
        <Button
          data-testid="visual-view-btn"
          variant={viewMode === 'visual' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('visual')}
          className={viewMode === 'visual' ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'text-zinc-700'}
        >
          <Eye className="w-4 h-4 mr-2" /> Visual
        </Button>
        <Button
          data-testid="technical-view-btn"
          variant={viewMode === 'technical' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('technical')}
          className={viewMode === 'technical' ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'text-zinc-700'}
        >
          <Code className="w-4 h-4 mr-2" /> Technical
        </Button>
      </div>
    </div>
  );
};

export default PreviewToolbar;
